<script setup lang="ts">
import { LeaderboardState } from '@/composition/leaderboard'
import { UserState } from '@/composition/users'
import { TimeFrame } from '@/models/Leaderboard'
import { IUser } from '@/models/Users'
import {
  LEADERBOARD_1ST_POSITION_ICON,
  LEADERBOARD_2ND_POSITION_ICON,
  LEADERBOARD_3RD_POSITION_ICON,
} from '@/util/const'
import { PropType, computed, onMounted, ref } from 'vue'
import LeaderboardSection from './LeaderboardSection.vue'

const props = defineProps({
  timeframe: {
    type: String as PropType<TimeFrame>,
    default: TimeFrame.THIRTY_DAY,
  },
  rowLimit: {
    type: Number,
    required: false,
  },
  isInfoCard: {
    type: Boolean,
    default: false,
  },
  showAllTimeFrames: {
    type: Boolean,
    default: false,
  },
})

// do not change the values of the options they are enum values used in the backend
const currentUser = ref<IUser | undefined>(undefined)
const isLoading = ref<boolean>(true)
onMounted(async () => {
  try {
    await LeaderboardState.fetchLeaderboard()
    if (UserState.state.currentUser?.contactId) {
      currentUser.value = await UserState.getUserByContactId(
        UserState.state.currentUser.contactId
      )
    }
  } finally {
    isLoading.value = false
  }
})
const currentUser7DLeaderboardRank = computed(() => {
  const currentUserRank =
    UserState.state.currentUser?.gamificationMeta?.leaderboard?.get(
      TimeFrame.SEVEN_DAY
    )
  if (currentUserRank !== undefined && currentUserRank > 10) {
    const leaderboardRow = {
      _id: '',
      user: {
        avatar: UserState.state.currentUser?.avatar,
        fullName: UserState.state.currentUser!.fullName,
        slug: UserState.state.currentUser!.slug!,
        contactId: UserState.state.currentUser!.contactId!,
      },
      totalPoints: currentUser.value?.gamificationMeta?.points ?? 0,
      rank: currentUserRank,
      updatedAt: '',
      timeFrame: TimeFrame.SEVEN_DAY,
    }
    return leaderboardRow
  }
  return undefined
})
const currentUser30DLeaderboardRank = computed(() => {
  const currentUserRank =
    UserState.state.currentUser?.gamificationMeta?.leaderboard?.get(
      TimeFrame.THIRTY_DAY
    )
  if (currentUserRank !== undefined && currentUserRank > 10) {
    const leaderboardRow = {
      _id: '',
      user: {
        avatar: UserState.state.currentUser?.avatar,
        fullName: UserState.state.currentUser!.fullName,
        slug: UserState.state.currentUser!.slug!,
        contactId: UserState.state.currentUser!.contactId!,
      },
      totalPoints: currentUser.value?.gamificationMeta?.points ?? 0,
      rank: currentUserRank,
      updatedAt: '',
      timeFrame: TimeFrame.THIRTY_DAY,
    }
    return leaderboardRow
  }
  return undefined
})
const currentUserAllTimeLeaderboardRank = computed(() => {
  const currentUserRank =
    UserState.state.currentUser?.gamificationMeta?.leaderboard?.get(
      TimeFrame.ALL_TIME
    )
  if (currentUserRank !== undefined && currentUserRank > 10) {
    const leaderboardRow = {
      _id: '',
      user: {
        avatar: UserState.state.currentUser?.avatar,
        fullName: UserState.state.currentUser!.fullName,
        slug: UserState.state.currentUser!.slug!,
        contactId: UserState.state.currentUser!.contactId!,
      },
      totalPoints: currentUser.value?.gamificationMeta?.points ?? 0,
      rank: currentUserRank,
      updatedAt: '',
      timeFrame: TimeFrame.ALL_TIME,
    }
    return leaderboardRow
  }
  return undefined
})

const leaderboard7d = computed(() => {
  let selectedLeaderboard = LeaderboardState.state.leaderboard7d

  selectedLeaderboard = selectedLeaderboard?.map((member: any) => {
    if (member.rank === 1) {
      member.rankIconBackground = LEADERBOARD_1ST_POSITION_ICON
    } else if (member.rank === 2) {
      member.rankIconBackground = LEADERBOARD_2ND_POSITION_ICON
    } else if (member.rank === 3) {
      member.rankIconBackground = LEADERBOARD_3RD_POSITION_ICON
    }
    return member
  })
  if (props.rowLimit) {
    return selectedLeaderboard?.slice(0, props.rowLimit)
  } else {
    return selectedLeaderboard
  }
})
const leaderboard30d = computed(() => {
  let selectedLeaderboard = LeaderboardState.state.leaderboard30d

  selectedLeaderboard = selectedLeaderboard?.map((member: any) => {
    if (member.rank === 1) {
      member.rankIconBackground = LEADERBOARD_1ST_POSITION_ICON
    } else if (member.rank === 2) {
      member.rankIconBackground = LEADERBOARD_2ND_POSITION_ICON
    } else if (member.rank === 3) {
      member.rankIconBackground = LEADERBOARD_3RD_POSITION_ICON
    }
    return member
  })
  if (props.rowLimit) {
    return selectedLeaderboard?.slice(0, props.rowLimit)
  } else {
    return selectedLeaderboard
  }
})
const leaderboardAlltime = computed(() => {
  let selectedLeaderboard = LeaderboardState.state.leaderboardAllTime

  selectedLeaderboard = selectedLeaderboard?.map((member: any) => {
    if (member.rank === 1) {
      member.rankIconBackground = LEADERBOARD_1ST_POSITION_ICON
    } else if (member.rank === 2) {
      member.rankIconBackground = LEADERBOARD_2ND_POSITION_ICON
    } else if (member.rank === 3) {
      member.rankIconBackground = LEADERBOARD_3RD_POSITION_ICON
    }
    return member
  })
  if (props.rowLimit) {
    return selectedLeaderboard?.slice(0, props.rowLimit)
  } else {
    return selectedLeaderboard
  }
})

const shouldRenderTimeFrame = (frame: TimeFrame) => {
  return props.showAllTimeFrames || props.timeframe === frame
}
</script>

<template>
  <div class="w-full">
    <div class="flex flex-row justify-between gap-6">
      <LeaderboardSection
        v-if="shouldRenderTimeFrame(TimeFrame.SEVEN_DAY)"
        :is-loading="isLoading"
        :timeframe="TimeFrame.SEVEN_DAY"
        :is-info-card="props.isInfoCard"
        :leaderboard-data="leaderboard7d"
        :current-user-rank="currentUser7DLeaderboardRank"
      />
      <LeaderboardSection
        v-if="shouldRenderTimeFrame(TimeFrame.THIRTY_DAY)"
        :is-loading="isLoading"
        :timeframe="TimeFrame.THIRTY_DAY"
        :is-info-card="props.isInfoCard"
        :leaderboard-data="leaderboard30d"
        :current-user-rank="currentUser30DLeaderboardRank"
      />
      <LeaderboardSection
        v-if="shouldRenderTimeFrame(TimeFrame.ALL_TIME)"
        :is-loading="isLoading"
        :timeframe="TimeFrame.ALL_TIME"
        :is-info-card="props.isInfoCard"
        :leaderboard-data="leaderboardAlltime"
        :current-user-rank="currentUserAllTimeLeaderboardRank"
      />
    </div>
  </div>
</template>
<style scoped>
.n-divider:not(.n-divider--vertical) {
  margin-top: 16px;
  margin-bottom: 14px;
}
</style>
